import { useState } from 'react'
import { useGetMobileApps } from '@/react/hooks'
import { ApplicationConfig } from '@/react/types/applications'
import * as React from 'react'
import { useAngularServices } from '@/react/components'
import { GeneralSettings, GenericClient, PossibleRoles } from '@/react/types'
import { navTab } from '@screens/Navigation/types'

import AdministrationIcon from '../icons/AdministrationIcon.svg'
import AssessmentIcon from '@/assets/icons/navigation/assessment-icon.svg'
import MobileIcon from '@/assets/icons/navigation/mobile-icon.svg'

interface CustomPermission {
  content_type: string
}

interface User {
  client: GenericClient
  custom_permissions?: CustomPermission[]
}

export const useGetNavData = (): navTab[] | null => {
  const { CurrentUser } = useAngularServices()

  const [user] = useState(CurrentUser.getInstance())
  const [client] = useState<GenericClient>(user.client)
  const [role] = useState<PossibleRoles>(CurrentUser.getRole())
  const [clientSettings] = useState<GeneralSettings>(
    CurrentUser.getClientSettings(),
  )
  const [apps] = useState<ApplicationConfig[]>(useGetMobileApps())

  const getNavData = (
    user: User,
    client: GenericClient,
    role: PossibleRoles,
    apps: ApplicationConfig[],
  ): navTab[] | [] => {
    const localNavData = []

    if (
      ['client_admin', 'supervisor'].includes(role) &&
      client.risk_dashboard_is_active
    ) {
      localNavData.push({
        text: 'Risk Dashboard',
        active: ['index', 'index.dashboard'],
        link: 'index.dashboard',
        icon: <i className="fa fa-dashboard menu-icon"></i>,
      })
    }

    if (
      ['client_admin'].includes(role) ||
      (user.custom_permissions &&
        user.custom_permissions.some(
          (permission: CustomPermission) => permission.content_type === 'user',
        ))
    ) {
      const localTab: navTab = {
        text: 'Administration',
        active: ['index'],
        activeExcludes: ['admin.templates'],
        icon: <img src={AdministrationIcon} />,
        submenu: [
          user.custom_permissions?.some(
            (permission: CustomPermission) =>
              permission.content_type === 'user',
          )
            ? null
            : {
                link: 'admin.projects.list',
                text: clientSettings.projects_language,
                active: [
                  'admin.projects',
                  'admin.projects.add',
                  'admin.projects.bulkUpload',
                  'admin.projects.list',
                  'admin.projects.edit',
                ],
              },
          user.custom_permissions?.some(
            (permission: CustomPermission) =>
              permission.content_type === 'user',
          )
            ? null
            : {
                text: 'Settings',
                active: [
                  'admin.company',
                  'admin.procore',
                  'admin.service_accounts',
                  'admin.service_accounts_view',
                  'admin.service_accounts_create',
                  'admin.service_accounts_edit',
                  'admin.general_settings',
                  'admin.disclaimer',
                ],
                submenu: [
                  {
                    text: 'Company Information',
                    link: 'admin.company.billing',
                    active: ['admin.company.billing'],
                  },
                  {
                    text: 'Disclaimer',
                    link: 'admin.company.disclaimer',
                    active: ['admin.company.disclaimer'],
                  },
                  {
                    text: 'Integrations',
                    active: [
                      'admin.procore',
                      'admin.service_accounts',
                      'admin.service_accounts_view',
                      'admin.service_accounts_create',
                      'admin.service_accounts_edit',
                    ],
                    submenu: [
                      {
                        text: 'Procore',
                        link: 'admin.procore',
                        active: ['admin.procore'],
                      },
                      {
                        text: 'Service Accounts',
                        link: 'admin.service_accounts',
                        active: [
                          'admin.service_accounts',
                          'admin.service_accounts_view',
                          'admin.service_accounts_create',
                          'admin.service_accounts_edit',
                        ],
                      },
                    ],
                  },
                  {
                    text: 'Logo',
                    link: 'admin.company.logo',
                    active: ['admin.company.logo'],
                  },
                ],
              },
          {
            text: 'Users',
            active: ['admin.users'],
            submenu: [
              {
                text: 'Add User',
                link: 'admin.users.add',
                active: ['admin.users.add'],
              },
              role === 'client_admin'
                ? {
                    text: 'Bulk Upload Users',
                    link: 'admin.users.bulkUpload',
                    active: ['admin.users.bulkUpload'],
                  }
                : null,
              {
                text: 'List Users',
                link: 'admin.users.list',
                active: ['admin.users.list'],
              },
              {
                text: 'Roles',
                link: 'admin.users.permissions',
                active: ['admin.users.permissions'],
              },
            ],
          },
          user.custom_permissions?.some(
            (permission: CustomPermission) =>
              permission.content_type === 'user',
          )
            ? null
            : {
                text: clientSettings.party_observed_language,
                link: 'admin.party_observed_list',
                active: [
                  'admin.party_observed_list',
                  'admin.party_observed_create',
                  'admin.party_observed_bulk',
                ],
              },
          apps[0]?.has_timecard ||
          apps[1]?.has_timecard ||
          apps[2]?.has_timecard
            ? user.custom_permissions?.some(
                (permission: CustomPermission) =>
                  permission.content_type === 'user',
              )
              ? null
              : {
                  text: 'Cost Codes',
                  link: 'admin.cost_codes_list',
                  active: [
                    'admin.cost_codes_list',
                    'admin.cost_codes_create',
                    'admin.cost_codes_bulk',
                  ],
                }
            : null,
        ],
      }
      localNavData.push(localTab)
    }
    if (
      ['client_admin', 'client_user', 'supervisor'].includes(role) &&
      apps.length
    ) {
      apps.forEach((app) => {
        const localTab: navTab = {
          icon: <img src={MobileIcon} alt="Mobile" />,
          text: app.name,
          active: ['app'],
          app: app.id,
          submenu: [
            app.has_company_news
              ? {
                  text: 'Company News',
                  link: 'app.company_news.list',
                  active: [
                    'app.company_news.list',
                    'app.company_news.add',
                    'app.company_news.edit',
                  ],
                  params: { app: app.id },
                  app: app.id,
                }
              : null,
            app.has_file_cabinet
              ? {
                  text: app.app_file_cabinet,
                  link: 'app.file_cabinet',
                  active: ['app.file_cabinet'],
                  params: { app: app.id },
                  app: app.id,
                }
              : null,
            app.has_mobile_forms
              ? {
                  text: app.app_mobile_form_display,
                  active: [],
                  app: app.id,
                  submenu: [
                    {
                      text: 'View Reports',
                      link: 'app.forms.reports',
                      active: [
                        'app.forms.reports',
                        'app.forms.add_report',
                        'app.forms.download_report_data',
                        'app.forms.deleted_reports',
                        'app.forms.draft_reports_list',
                        'app.forms.reports_view',
                        'app.forms.reports_edit',
                        'app.forms.reports_create',
                        'app.forms.reports_export_list',
                      ],
                      params: { app: app.id },
                      app: app.id,
                    },
                    ['client_admin', 'supervisor'].includes(role)
                      ? {
                          text: 'Create ' + app.app_mobile_form_display,
                          link: 'app.forms.create',
                          active: ['app.forms.create'],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                    ['client_admin', 'supervisor'].includes(role)
                      ? {
                          text:
                            'Assign ' +
                            app.app_mobile_form_display +
                            ' to ' +
                            app.project,
                          link: 'app.forms.assign',
                          active: ['app.forms.assign'],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                    ['client_admin', 'supervisor'].includes(role)
                      ? {
                          text:
                            'Assign ' +
                            app.project +
                            ' to ' +
                            app.app_mobile_form_display,
                          link: 'app.forms.list',
                          active: ['app.forms.list'],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                    ['client_admin'].includes(role)
                      ? {
                          text: app.app_mobile_form_display + ' Groups',
                          link: 'app.forms.custom',
                          active: ['app.forms.custom'],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                    ['client_admin', 'supervisor'].includes(role)
                      ? {
                          text: 'QR Codes',
                          link: 'app.forms.qr_codes',
                          active: ['app.forms.qr_codes'],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                    ['client_admin'].includes(role)
                      ? {
                          text: 'Settings',
                          link: 'app.forms.settings',
                          active: ['app.forms.settings'],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                  ],
                }
              : null,
            app.has_observations
              ? {
                  text: app.observations_display,
                  active: [
                    'app.observations',
                    'app.questions',
                    'app.categories',
                    'app.batch_reports',
                  ],
                  params: { app: app.id },
                  app: app.id,
                  basePath: `/app/${app.id}/observations`,
                  submenu: [
                    {
                      text: 'Reports',
                      link: 'app.observations.list',
                      active: [
                        'app.observations.list',
                        'app.observations.add',
                        'app.observations.deleted_observations',
                        'app.observation_templates',
                        'app.observations.edit',
                        'app.observations.view',
                      ],
                      params: { app: app.id },
                      app: app.id,
                    },
                    ['client_admin', 'trainee_manager'].includes(role)
                      ? {
                          text: app.categories,
                          link: 'app.categories.custom',
                          active: [
                            'app.categories.custom',
                            'app.categories.list',
                            'app.categories.by_project',
                            'app.categories.groups',
                            'app.categories.group_assign',
                            'app.categories.assign_projects',
                            'app.categories.assign',
                          ],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                    ['client_admin', 'supervisor', 'trainee_manager'].includes(
                      role,
                    )
                      ? {
                          text: 'Questions',
                          link: 'app.questions.list',
                          active: [
                            'app.questions.list',
                            'app.questions.add',
                            'app.questions.custom_categories',
                          ],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                  ],
                }
              : null,
            app.has_procedures &&
            ['client_admin', 'client_user', 'supervisor'].includes(role)
              ? {
                  text: app.app_procedure_display,
                  link: 'app.procedures.list',
                  active: ['app.procedures.list', 'app.procedures.assign'],
                  params: { app: app.id },
                  app: app.id,
                }
              : null,
            app.has_process_flows &&
            ['client_admin', 'client_user', 'supervisor'].includes(role)
              ? {
                  text: app.app_process_flow_display,
                  link: 'app.process_flows.list',
                  active: [
                    'app.process_flows.list',
                    'app.process_flows.assign',
                  ],
                  params: { app: app.id },
                  app: app.id,
                }
              : null,
            app.has_sds
              ? {
                  text: app.safety_data_sheets_display,
                  link: 'app.safety_data_sheets_list',
                  active: [
                    'app.safety_data_sheets_list',
                    'app.safety_data_sheets_create',
                    'app.safety_data_sheets_archive',
                  ],
                  params: { app: app.id },
                  app: app.id,
                }
              : null,
            app.has_timecard && ['client_admin', 'supervisor'].includes(role)
              ? {
                  text: app.main_menu_timecard,
                  link: 'app.timecard.current_page',
                  active: [
                    'app.timecard.current_page',
                    'app.timecard.settings',
                    'app.timecard.daily_reports',
                    'app.timecard.timesheet',
                  ],
                  params: { app: app.id },
                  app: app.id,
                }
              : null,
            app.has_toolbox_talks
              ? {
                  text: app.toolboxes,
                  active: ['app.toolboxes'],
                  params: { app: app.id },
                  app: app.id,
                  submenu: [
                    {
                      text: 'Reports',
                      link: 'app.toolboxes.list',
                      active: [
                        'app.toolboxes.list',
                        'app.toolboxes.deleted',
                        'app.toolboxes.view',
                      ],
                      params: { app: app.id },
                      app: app.id,
                    },
                    ['client_admin', 'supervisor'].includes(role) &&
                    role !== 'client_user'
                      ? {
                          text: 'Templates',
                          link: 'app.toolboxes.toolbox_templates',
                          active: [
                            'app.toolboxes.toolbox_templates',
                            'app.toolboxes.edit',
                            'app.toolboxes.toolbox_templates_view',
                            'app.toolboxes.add',
                            'app.toolboxes.project_to_toolbox',
                            'app.toolboxes.assign_projects',
                            'app.toolboxes.toolbox_to_project',
                            'app.toolboxes.assign',
                            'app.toolboxes.groups',
                            'app.toolboxes.group_assign',
                          ],
                          params: { app: app.id },
                          app: app.id,
                        }
                      : null,
                  ],
                }
              : null,
          ],
        }
        localNavData.push(localTab)
      })
    }
    if (
      client.lessons_is_active &&
      ['client_admin', 'client_user', 'supervisor'].includes(role)
    ) {
      const localTab: navTab = {
        icon: (
          <i
            className="fa fa-lightbulb-o menu-icon"
            style={{ fontSize: '1.4em' }}
          ></i>
        ),
        text: 'Lessons Learned',
        active: ['lessons.list', 'lessons.edit', 'lessons.add'],
        link: 'lessons.list',
      }
      localNavData.push(localTab)
    }
    if (
      client.training_is_active &&
      [
        'client_admin',
        'client_user',
        'read_only',
        'supervisor',
        'trainee',
        'trainee_manager',
      ].includes(role)
    ) {
      const localTab: navTab = {
        icon: (
          <i
            className="glyphicon glyphicon-blackboard"
            style={{ fontSize: '1.4em' }}
            aria-hidden="true"
          ></i>
        ),
        text: 'Training',
        active: ['trainings'],
        submenu: [
          {
            text: 'Training Documentation',
            active: [
              'trainings.documentation',
              'trainings.add',
              'trainings.import',
              'trainings.deleted',
              'trainings.settings',
              'trainings.edit',
            ],
            link: 'trainings.documentation',
          },
          {
            text: 'Learning Management System',
            active: [
              'trainings.program',
              'trainings.manage',
              'trainings.topics',
              'trainings.preview_content',
              'trainings.trainee_quiz',
              'trainings.topic',
              'trainings.quiz.edit',
              'trainings.assign.employees_list',
              'trainings.assign.trainings_list',
              'trainings.assign.training_assign',
              'trainings.assign.employee_assign',
              'trainings.assign.job_trainings_groups_list',
              'trainings.assign.employee_groups_list',
              'trainings.assign.job_trainings_groups_assign',
              'trainings.assign.employee_groups_assign',
            ],
            link: [
              'trainee',
              'readonly_user',
              'supervisor',
              'client_user',
              'read_only',
            ].includes(role)
              ? 'trainings.program'
              : 'trainings.manage',
          },
        ],
      }
      localNavData.push(localTab)
    }
    if (client.assessment_is_active && ['client_admin'].includes(role)) {
      const localTab: navTab = {
        icon: <img src={AssessmentIcon} alt="Assessments" />,
        text: 'Assessments',
        active: [
          'assessment.assessment_manage',
          'assessment.assessment_edit',
          'assessment.assessment_score_key',
          'assessment.assessment_create',
          'assessment.assessment_completed',
          'assessment.assessment_draft',
        ],
        link: 'assessment.assessment_manage',
      }
      localNavData.push(localTab)
    }
    if (['admin'].includes(role)) {
      const localTab: navTab = {
        icon: (
          <div className="menu-icon-div">
            <i className="fa fa-users menu-icon"></i>
          </div>
        ),
        text: 'Accounts',
        active: [
          'true_admin.admin_table',
          'true_admin.admin_clients',
          'true_admin.admin_clients_add',
          'true_admin.admin_add',
          'true_admin.admin_edit',
          'true_admin.admin_clients_edit',
        ],
        link: 'true_admin.admin_table',
      }
      localNavData.push(localTab)
    }

    return localNavData
  }

  return getNavData(user, client, role, apps)
}
